import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  ViewChild
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import {
  type IRecurringTask,
  IRecurringTaskSlot
} from '@appgen-health-crm/api-interfaces';
import { JsonEditorComponent } from 'ang-jsoneditor';
import { addMinutes } from 'date-fns';
import { takeWhile } from 'rxjs/operators';

interface ITimeSlot {
  sDate: Date;
  s: number;
  error?: string;
}
@Component({
  selector: 'recurring-task',
  templateUrl: './recurring-task.component.html',
  styleUrls: ['./recurring-task.component.less']
})
export class RecurringTaskComponent implements OnInit, OnDestroy {
  private _edit: boolean;
  active = true;
  @Input() recurringTask: IRecurringTask;
  @Input() version: number;
  @Input() set edit(v: boolean) {
    v ? this.jsonEditor?.setMode('code') : this.jsonEditor?.setMode('view');
    this._edit = v;
  }
  get edit() {
    return this._edit;
  }

  @Output() saveRecurringTask = new EventEmitter<IRecurringTask>();
  _recurringTask: IRecurringTask;
  weekMap: {
    [key: number]: { name: string; slots: ITimeSlot[] };
  } = this.convertSlotsToWeekMap([]);
  @ViewChild('jsonEditor') jsonEditor: JsonEditorComponent;

  errors = { name: null, startDate: null, endDate: null };

  listOfColumn = [
    {
      title: 'Date'
    },
    {
      title: 'Start Time'
    },
    {
      title: 'End Time'
    },
    {
      title: 'Available'
    },
    {
      title: 'Actions',
      width: '100px'
    }
  ];

  dateFormat = 'yyyy/MM/dd';
  disabledDate = (date: Date): boolean => {
    if (date > new Date()) {
      return false;
    }
    return true;
  };

  jsonCtrl = new FormControl();

  constructor(private fb: FormBuilder) {}

  ngOnInit(): void {
    this.reset();

    this.jsonCtrl.valueChanges
      .pipe(takeWhile(() => this.active))
      .subscribe(json => {
        this._recurringTask.template = json;
      });
  }

  ngOnDestroy() {
    this.active = false;
  }

  convertSlotsToWeekMap(slots: IRecurringTaskSlot[]) {
    const map = {
      0: { name: 'Sunday', alias: 'Sun', slots: [] },
      1: { name: 'Monday', alias: 'Mon', slots: [] },
      2: { name: 'Tuesday', alias: 'Tue', slots: [] },
      3: { name: 'Wednesday', alias: 'Wed', slots: [] },
      4: { name: 'Thursday', alias: 'Thu', slots: [] },
      5: { name: 'Friday', alias: 'Fri', slots: [] },
      6: { name: 'Saturday', alias: 'Sat', slots: [] }
    };
    slots.forEach(slot => {
      if (map[slot.weekDay]) {
        map[slot.weekDay].slots.push({
          sDate: this.convertMinsToDate(slot.startMinute),
          s: slot.startMinute
        });
      }
    });
    return map;
  }

  convertMinsToDate(mins: number): Date {
    return addMinutes(new Date().setHours(0, 0, 0, 0), mins);
  }

  convertDateToMins(date: Date): number {
    return date.getHours() * 60 + date.getMinutes();
  }

  save(): boolean {
    if (!this.validate()) {
      return false;
    }
    const slots: IRecurringTaskSlot[] = [];

    for (const key in this.weekMap) {
      if (Object.prototype.hasOwnProperty.call(this.weekMap, key)) {
        const el = this.weekMap[key];
        el.slots.forEach(slot => {
          slots.push({
            startMinute: slot.s,
            weekDay: +key
          });
        });
      }
    }

    const recurringTask: IRecurringTask = {
      ...this._recurringTask,
      slots
    };

    this.saveRecurringTask.emit(recurringTask);
    return true;
  }

  validate(): boolean {
    let valid = true;

    /* ------------------------ validate required fields ------------------------ */
    if (!this._recurringTask.label || !this._recurringTask.label.trim()) {
      this.errors.name = 'error';
      valid = false;
    } else if (!this._recurringTask.startDate || !this._recurringTask.endDate) {
      valid = false;
      this.errors.startDate = 'error';
    } else if (!this._recurringTask.endDate) {
      valid = false;
      this.errors.endDate = 'error';
    } else {
      this.errors = { name: null, startDate: null, endDate: null };
    }
    if (!valid) return valid;
    /* ----------------------------- validate slots ----------------------------- */

    for (const key in this.weekMap) {
      if (Object.prototype.hasOwnProperty.call(this.weekMap, key)) {
        const day = this.weekMap[key];
        let dayValid = true;

        // validate empty slots
        for (let i = 0; i < day.slots.length; i++) {
          const slot = day.slots[i];
          if (!slot.sDate || !(slot.sDate instanceof Date)) {
            slot.error = 'Slot start time is required';
            dayValid = false;
          }
        }
        if (!dayValid) {
          valid = false;
          continue;
        }

        // convert slots date to mins
        for (let i = 0; i < day.slots.length; i++) {
          const slot = day.slots[i];

          slot.s = this.convertDateToMins(slot.sDate);
        }

        if (!dayValid) {
          valid = false;
          continue;
        }

        // validate overlapping slots
        day.slots = day.slots.sort((a, b) => a.s - b.s);

        for (let i = 1; i < day.slots.length; i++) {
          const slot = day.slots[i];
          const prevSlot = day.slots[i - 1];
          if (slot.s == prevSlot.s) {
            slot.error = 'This slot is overlapping with the previous slot';
            dayValid = false;
          } else {
            slot.error = null;
          }
        }
        if (!dayValid) valid = false;
      }
    }
    return valid;
  }

  removeSlot(key: string, index: number) {
    this.weekMap[key].slots.splice(index, 1);
  }

  updateSlot(key: string, index: number, e: { sDate?: Date; eDate?: Date }) {
    this.weekMap[key].slots[index] = {
      ...this.weekMap[key].slots[index],
      sDate: e.sDate,
      error: null
    };
  }

  addSlot(key) {
    this.weekMap[key].slots.push({
      sDate: null,
      s: null
    });
  }

  reset() {
    this._recurringTask = {
      ...this.recurringTask,
      slots: [...this.recurringTask.slots]
    };
    this.weekMap = this.convertSlotsToWeekMap(this._recurringTask.slots);
    this.jsonCtrl.setValue(this._recurringTask.template);
  }

  updateExtras(json: any) {
    if (json instanceof Event) return;
    this._recurringTask.template = json;
  }
}
